import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Gift, Share2, Star, Copy, CheckCircle2, Plus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatBox({ label, value, money, tone }) {
  const tones = { gold: "text-[#F2B705]", ok: "text-green-400" };
  return (
    <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-4">
      <div className="text-white/45 text-xs uppercase tracking-widest">{label}</div>
      <div className={`font-display font-black text-3xl mt-1.5 ${tones[tone] || "text-white"}`}>{money ? `$${value}` : value}</div>
    </div>
  );
}

function Referrals({ token }) {
  const [data, setData] = useState(null);
  const [gen, setGen] = useState({ email: "", name: "" });
  const [copied, setCopied] = useState("");

  const load = () => axios.get(`${API}/referrals`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => setData(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const generate = async () => {
    if (!gen.email.trim()) { toast.error("Enter the customer's email."); return; }
    try { await axios.post(`${API}/referrals/generate`, gen, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Referral code created."); setGen({ email: "", name: "" }); load(); }
    catch { toast.error("Could not create code."); }
  };
  const redeem = async (id) => {
    try { await axios.post(`${API}/referrals/events/${id}/redeem`, {}, { headers: { Authorization: `Bearer ${token}` } }); toast.success("Marked redeemed."); load(); }
    catch { toast.error("Failed."); }
  };
  const copy = (code) => { navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(""), 1500); };

  if (!data) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#F2B705]" /></div>;

  return (
    <div className="space-y-5" data-testid="rewards-referrals">
      <p className="text-white/55 text-sm">Each customer gets a code. A new customer using it gets <b className="text-[#F2B705]">${data.reward} off</b>, and the referrer earns <b className="text-[#F2B705]">500 Drivers Club points</b>.</p>
      <div className="grid grid-cols-2 gap-3">
        <StatBox label="Total Referrals" value={data.stats.total_referrals} />
        <StatBox label="Credit Outstanding" value={data.stats.credit_outstanding} money tone="gold" />
      </div>

      <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[140px]">
          <label className="text-xs uppercase tracking-widest text-white/45">Customer Email</label>
          <input data-testid="referral-gen-email" value={gen.email} onChange={(e) => setGen({ ...gen, email: e.target.value })} className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2" placeholder="customer@email.com" />
        </div>
        <div className="flex-1 min-w-[120px]">
          <label className="text-xs uppercase tracking-widest text-white/45">Name</label>
          <input data-testid="referral-gen-name" value={gen.name} onChange={(e) => setGen({ ...gen, name: e.target.value })} className="w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2" placeholder="Optional" />
        </div>
        <button data-testid="referral-gen-btn" onClick={generate} className="flex items-center gap-2 bg-[#F2B705] text-black font-bold px-4 py-2 rounded-md hover:bg-white transition-colors"><Plus size={16} /> Create Code</button>
      </div>

      {data.codes.length > 0 && (
        <div className="space-y-2" data-testid="referral-codes">
          {data.codes.map((c) => (
            <div key={c.code} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <button onClick={() => copy(c.code)} className="font-mono font-bold text-[#F2B705] flex items-center gap-1.5">{c.code} {copied === c.code ? <CheckCircle2 size={13} className="text-green-400" /> : <Copy size={12} />}</button>
                <div className="text-white/45 text-xs">{c.name || c.email}</div>
              </div>
              <div className="text-xs text-white/60 flex gap-4">
                <span>{c.signups} signups</span>
                <span>Earned: <b className="text-white/80">${c.credit_earned}</b></span>
                <span>Available: <b className="text-green-400">${c.credit_available}</b></span>
              </div>
            </div>
          ))}
        </div>
      )}

      {data.events.length > 0 && (
        <div>
          <h4 className="text-white/70 font-semibold text-sm mb-2">Referral Activity</h4>
          <div className="space-y-2" data-testid="referral-events">
            {data.events.map((e) => (
              <div key={e.id} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5 flex flex-wrap items-center justify-between gap-2 text-sm">
                <span><b className="text-white/85">{e.new_name}</b> used <span className="font-mono text-[#F2B705]">{e.code}</span> <span className="text-white/40">(referrer: {e.referrer_name || e.referrer_email})</span></span>
                <div className="flex items-center gap-3">
                  <span className="text-white/50 text-xs">{e.referrer_points ? `Referrer: +${e.referrer_points} club pts` : `Referrer credit: $${e.referrer_credit}`}</span>
                  {e.redeemed ? <span className="text-xs text-green-400">Redeemed</span> : <button data-testid={`referral-redeem-${e.id}`} onClick={() => redeem(e.id)} className="text-xs border border-[#F2B705]/50 text-[#F2B705] px-2.5 py-1 rounded hover:bg-[#F2B705] hover:text-black transition-colors">Mark Redeemed</button>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Loyalty({ token }) {
  const H = { headers: { Authorization: `Bearer ${token}` } };
  const [rows, setRows] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const load = () => axios.get(`${API}/drivers-club/members`, H).then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post(`${API}/drivers-club/sync`, {}, H);
      toast.success(data.new_members > 0 ? `${data.new_members} past renter(s) enrolled in the Drivers Club.` : "Everyone's already enrolled.");
      load();
    } catch { toast.error("Sync failed."); }
    finally { setSyncing(false); }
  };

  if (!rows) return <div className="flex justify-center py-10"><Loader2 className="animate-spin text-[#F2B705]" /></div>;
  const totalPts = rows.reduce((a, m) => a + (m.points || 0), 0);

  return (
    <div className="space-y-5" data-testid="rewards-loyalty">
      <p className="text-white/55 text-sm">
        The <b className="text-[#F2B705]">Drivers Club</b> is your one rewards system: members earn <b className="text-[#F2B705]">2 pts per $1 spent</b>,
        +250 on-time return, +500 per referral, +500 per 5-star review. Every renter is auto-enrolled.
        Redeem rewards and adjust points in <b className="text-white/80">Marketing → Drivers Club</b>.
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="grid grid-cols-2 gap-3 flex-1 max-w-md">
          <StatBox label="Club Members" value={rows.length} />
          <StatBox label="Points Outstanding" value={totalPts.toLocaleString()} tone="gold" />
        </div>
        <button data-testid="club-sync-btn" onClick={sync} disabled={syncing}
          className="flex items-center gap-2 px-4 py-2 rounded-md border border-[#F2B705]/40 text-[#F2B705] text-sm font-semibold hover:bg-[#F2B705] hover:text-black transition-colors disabled:opacity-50">
          {syncing ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Enroll Past Renters
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-center py-12 border border-white/10 rounded-xl bg-[#0A0A0A] text-white/50">No club members yet — tap "Enroll Past Renters" to bring everyone in.</div>
      ) : (
        <div className="space-y-2" data-testid="loyalty-list">
          {rows.map((m) => (
            <div key={m.id} className="bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-medium">{m.name || m.email || m.phone}</div>
                <div className="text-white/45 text-xs">{m.tier} · ${Number(m.spend || 0).toLocaleString()} spent · {m.referrals || 0} referral{(m.referrals || 0) === 1 ? "" : "s"}</div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span>Balance: <b className="text-[#F2B705]">{Number(m.points || 0).toLocaleString()} pts</b></span>
                <span>Lifetime: <b className="text-white/80">{Number(m.lifetime_points || 0).toLocaleString()}</b></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function RewardsManager({ token }) {
  const [sub, setSub] = useState("referrals");
  return (
    <div data-testid="rewards-dashboard" className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl flex items-center gap-2"><Gift className="text-[#F2B705]" size={22} /> Rewards</h2>
        <p className="text-white/45 text-sm mt-1">Referral program and loyalty rewards for your repeat customers.</p>
      </div>
      <div className="flex gap-2">
        <button data-testid="rewards-tab-referrals" onClick={() => setSub("referrals")} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${sub === "referrals" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}><Share2 size={15} /> Referrals</button>
        <button data-testid="rewards-tab-loyalty" onClick={() => setSub("loyalty")} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors ${sub === "loyalty" ? "bg-[#F2B705] text-black" : "bg-[#0C0C0C] border border-white/10 text-white/70"}`}><Star size={15} /> Loyalty</button>
      </div>
      {sub === "referrals" ? <Referrals token={token} /> : <Loyalty token={token} />}
    </div>
  );
}
